import React, { useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  Crown,
  Loader2,
  MinusCircle,
  Waves,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { formatTimeAgo } from './adminFormatters';
import { deriveGuidePartnersState } from './guidePartnersRoomModel';
import { buildListenLearnRoomContext } from './listenLearnRoomModel';
import { getAdminRoomById } from './adminRoomConfig';
import { getOperationalEvents, splitSiteEvents } from '../../lib/siteEvents';
import { toast } from 'sonner';
import ActionListSection from './command-center/ActionListSection';
import QuickAddActionDialog from './command-center/QuickAddActionDialog';
import QueueItemEditDialog from './command-center/QueueItemEditDialog';
import ResolvedTodaySection from './command-center/ResolvedTodaySection';
import LaterSection from './command-center/LaterSection';
import QueueCategorySection from './command-center/QueueCategorySection';
import CollapsibleSection from './command-center/CollapsibleSection';
import { getQueueItemNavigation } from './command-center/commandCenterQueueUtils';

const ZONE_ACCENTS = {
  communication: {
    panel: 'border-orange-200 dark:border-orange-500/25',
    icon: 'bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300',
    eyebrow: 'text-orange-600 dark:text-orange-300',
    topBar: 'from-orange-400 via-orange-300 to-amber-300 dark:from-orange-500/80 dark:via-orange-400/60 dark:to-amber-300/60',
    questionPanel: 'border-orange-100 bg-orange-50/80 dark:border-white/10 dark:bg-white/[0.03]',
  },
  'heart-work': {
    panel: 'border-aqua/30 dark:border-aqua/25',
    icon: 'bg-aqua/15 text-cyan-800 dark:bg-aqua/10 dark:text-aqua',
    eyebrow: 'text-cyan-700 dark:text-aqua',
    topBar: 'from-cyan-400 via-aqua to-emerald-300 dark:from-cyan-400/70 dark:via-aqua/70 dark:to-emerald-300/60',
    questionPanel: 'border-cyan-100 bg-cyan-50/70 dark:border-white/10 dark:bg-white/[0.03]',
  },
  'guide-partners': {
    panel: 'border-amber-200 dark:border-amber-400/25',
    icon: 'bg-amber-100 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300',
    eyebrow: 'text-amber-600 dark:text-amber-300',
    topBar: 'from-amber-300 via-yellow-300 to-orange-300 dark:from-amber-300/70 dark:via-yellow-300/60 dark:to-orange-300/60',
    questionPanel: 'border-amber-100 bg-amber-50/75 dark:border-white/10 dark:bg-white/[0.03]',
  },
  'advisory-partners': {
    panel: 'border-lime-200 dark:border-lime-400/25',
    icon: 'bg-lime-100 text-lime-700 dark:bg-lime-400/10 dark:text-lime-300',
    eyebrow: 'text-lime-600 dark:text-lime-300',
    topBar: 'from-lime-300 via-emerald-300 to-cyan-200 dark:from-lime-300/70 dark:via-emerald-300/60 dark:to-cyan-200/55',
    questionPanel: 'border-lime-100 bg-lime-50/75 dark:border-white/10 dark:bg-white/[0.03]',
  },
  'support-partners': {
    panel: 'border-pink-200 dark:border-pink-400/25',
    icon: 'bg-pink-100 text-pink-700 dark:bg-pink-400/10 dark:text-pink-300',
    eyebrow: 'text-pink-600 dark:text-pink-300',
    topBar: 'from-pink-300 via-rose-300 to-orange-200 dark:from-pink-300/70 dark:via-rose-300/60 dark:to-orange-200/55',
    questionPanel: 'border-pink-100 bg-pink-50/75 dark:border-white/10 dark:bg-white/[0.03]',
  },
  'super-fans': {
    panel: 'border-yellow-200 dark:border-yellow-400/25',
    icon: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-400/10 dark:text-yellow-300',
    eyebrow: 'text-yellow-600 dark:text-yellow-300',
    topBar: 'from-yellow-300 via-amber-300 to-orange-200 dark:from-yellow-300/70 dark:via-amber-300/60 dark:to-orange-200/55',
    questionPanel: 'border-yellow-100 bg-yellow-50/75 dark:border-white/10 dark:bg-white/[0.03]',
  },
  marketing: {
    panel: 'border-fuchsia-200 dark:border-fuchsia-400/25',
    icon: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-400/10 dark:text-fuchsia-300',
    eyebrow: 'text-fuchsia-600 dark:text-fuchsia-300',
    topBar: 'from-fuchsia-300 via-pink-300 to-rose-200 dark:from-fuchsia-300/70 dark:via-pink-300/60 dark:to-rose-200/55',
    questionPanel: 'border-fuchsia-100 bg-fuchsia-50/75 dark:border-white/10 dark:bg-white/[0.03]',
  },
  'free-users': {
    panel: 'border-violet-200 dark:border-violet-400/25',
    icon: 'bg-violet-100 text-violet-700 dark:bg-violet-400/10 dark:text-violet-300',
    eyebrow: 'text-violet-600 dark:text-violet-300',
    topBar: 'from-violet-300 via-purple-300 to-indigo-200 dark:from-violet-300/70 dark:via-purple-300/60 dark:to-indigo-200/55',
    questionPanel: 'border-violet-100 bg-violet-50/75 dark:border-white/10 dark:bg-white/[0.03]',
  },
  stewardship: {
    panel: 'border-emerald-200 dark:border-emerald-400/25',
    icon: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300',
    eyebrow: 'text-emerald-600 dark:text-emerald-300',
    topBar: 'from-emerald-300 via-emerald-200 to-lime-300 dark:from-emerald-300/70 dark:via-emerald-200/55 dark:to-lime-300/50',
    questionPanel: 'border-emerald-100 bg-emerald-50/75 dark:border-white/10 dark:bg-white/[0.03]',
  },
};

const FOCUS_TONE_CLASSES = {
  urgent: 'border-red-200 bg-red-50 text-red-950 dark:border-red-500/25 dark:bg-red-500/5 dark:text-red-200',
  attention: 'border-yellow-200 bg-yellow-50 text-yellow-950 dark:border-yellow-500/25 dark:bg-yellow-500/5 dark:text-yellow-100',
  next: 'border-aqua/30 bg-aqua/10 text-slate-900 dark:border-aqua/25 dark:bg-aqua/5 dark:text-white',
  steady: 'border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-400/20 dark:bg-slate-400/5 dark:text-white',
};

const STATUS_BADGE_CLASSES = {
  urgent: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-300',
  attention: 'border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-500/25 dark:bg-yellow-500/10 dark:text-yellow-300',
  next: 'border-aqua/30 bg-aqua/10 text-cyan-800 dark:border-aqua/25 dark:bg-aqua/10 dark:text-aqua',
  steady: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-400/20 dark:bg-slate-400/10 dark:text-slate-300',
};

const FOCUS_BADGE_CLASSES = {
  urgent: 'border-red-200 bg-red-100/80 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200',
  attention: 'border-yellow-200 bg-yellow-100/80 text-yellow-700 dark:border-yellow-500/20 dark:bg-yellow-500/10 dark:text-yellow-100',
  next: 'border-aqua/30 bg-aqua/15 text-cyan-800 dark:border-aqua/20 dark:bg-aqua/10 dark:text-aqua',
  steady: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-500/20 dark:bg-slate-500/10 dark:text-slate-200',
};

const FOCUS_NUMBER_CLASSES = {
  urgent: 'border-red-200 bg-red-100 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200',
  attention: 'border-yellow-200 bg-yellow-100 text-yellow-700 dark:border-yellow-500/30 dark:bg-yellow-500/10 dark:text-yellow-100',
  next: 'border-aqua/30 bg-aqua/15 text-cyan-800 dark:border-aqua/30 dark:bg-aqua/10 dark:text-aqua',
  steady: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200',
};

const FOCUS_EDGE_CLASSES = {
  urgent: 'from-red-400 via-red-300 to-red-200 dark:from-red-400 dark:via-red-300/80 dark:to-red-200/60',
  attention: 'from-yellow-400 via-yellow-300 to-orange-200 dark:from-yellow-400 dark:via-yellow-300/80 dark:to-orange-200/60',
  next: 'from-cyan-400 via-aqua to-emerald-300 dark:from-cyan-400 dark:via-aqua/80 dark:to-emerald-300/60',
  steady: 'from-slate-400 via-slate-300 to-slate-200 dark:from-slate-400 dark:via-slate-300/80 dark:to-slate-200/55',
};

const PATTERN_CARD_CLASSES = {
  urgent: 'border-[#7f6f64]/55 bg-[#253744] text-[#f5f0e8] shadow-[0_18px_38px_rgba(10,16,22,0.18)]',
  attention: 'border-[#8f8168]/55 bg-[#273b48] text-[#f5f0e8] shadow-[0_18px_38px_rgba(10,16,22,0.18)]',
  next: 'border-[#6f8798]/55 bg-[#233441] text-[#f5f0e8] shadow-[0_18px_38px_rgba(10,16,22,0.18)]',
  steady: 'border-[#5e7382]/45 bg-[#1f2d38] text-[#f5f0e8] shadow-[0_18px_38px_rgba(10,16,22,0.16)]',
};

const CRITICAL_PATH_PANEL_CLASSES = {
  urgent: 'border-[#8d7a70]/60 bg-[#2d4453] text-[#f5f0e8] shadow-[0_22px_50px_rgba(10,16,22,0.22)]',
  attention: 'border-[#9b8f72]/60 bg-[#304856] text-[#f5f0e8] shadow-[0_22px_50px_rgba(10,16,22,0.22)]',
  next: 'border-[#7290a5]/60 bg-[#27404f] text-[#f5f0e8] shadow-[0_22px_50px_rgba(10,16,22,0.22)]',
};

const CRITICAL_PATH_ICON_TONE_CLASSES = {
  urgent: 'border border-white/12 bg-[#f0c9b8]/12 text-[#f6d6ca]',
  attention: 'border border-white/12 bg-[#f1d99e]/12 text-[#f3dfb2]',
  next: 'border border-white/12 bg-[#9bd7d8]/12 text-[#b5ecea]',
};

const STRATEGIC_BADGE_CLASSES = {
  urgent: 'border-[#a8897e]/55 bg-black/20 text-[#f7ddd2]',
  attention: 'border-[#ad9d75]/55 bg-black/20 text-[#f6e4b9]',
  next: 'border-[#7fa5b9]/55 bg-black/20 text-[#d6edf2]',
  steady: 'border-white/15 bg-black/20 text-[#e8e1d7]',
};

const STRATEGIC_TEXTURE_STYLES = {
  strong: {
    backgroundImage: [
      'radial-gradient(120% 140% at 0% 0%, rgba(242, 228, 199, 0.14) 0%, rgba(242, 228, 199, 0) 45%)',
      'radial-gradient(140% 120% at 100% 100%, rgba(10, 16, 22, 0.2) 0%, rgba(10, 16, 22, 0) 52%)',
      'linear-gradient(145deg, rgba(97, 140, 169, 0.28), rgba(34, 49, 61, 0.14) 44%, rgba(17, 25, 32, 0.2))',
    ].join(', '),
    backgroundSize: '100% 100%, 100% 100%, 100% 100%',
    backgroundPosition: '0 0, 0 0, 0 0',
  },
  soft: {
    backgroundImage: [
      'radial-gradient(120% 140% at 0% 0%, rgba(239, 226, 201, 0.1) 0%, rgba(239, 226, 201, 0) 42%)',
      'radial-gradient(140% 120% at 100% 100%, rgba(10, 16, 22, 0.14) 0%, rgba(10, 16, 22, 0) 55%)',
      'linear-gradient(150deg, rgba(86, 124, 149, 0.18), rgba(24, 34, 42, 0.06) 46%, rgba(18, 26, 33, 0.14))',
    ].join(', '),
    backgroundSize: '100% 100%, 100% 100%, 100% 100%',
    backgroundPosition: '0 0, 0 0, 0 0',
  },
};

const LANE_PANEL_CLASSES = {
  urgent: 'border-red-200 bg-gradient-to-br from-red-50 via-white to-red-50/70 dark:border-red-500/15 dark:from-[#241617] dark:via-[#1b1415] dark:to-[#161112]',
  attention: 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50/70 dark:border-amber-500/15 dark:from-[#241d14] dark:via-[#1c1712] dark:to-[#141312]',
  next: 'border-sky-200 bg-gradient-to-br from-sky-50 via-white to-cyan-50/70 dark:border-sky-500/15 dark:from-[#151d21] dark:via-[#11181d] dark:to-[#0f1418]',
  steady: 'border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-50/70 dark:border-white/10 dark:from-[#20232a] dark:via-[#171b20] dark:to-[#11161a]',
};

const LANE_ITEM_CLASSES = {
  urgent: 'border-red-200 bg-white/90 dark:border-red-500/20 dark:bg-black/10',
  attention: 'border-amber-200 bg-white/90 dark:border-amber-500/20 dark:bg-black/10',
  next: 'border-sky-200 bg-white/90 dark:border-sky-500/20 dark:bg-black/10',
  steady: 'border-slate-200 bg-white/90 dark:border-white/10 dark:bg-black/10',
};

const SIGNAL_ICON_BY_TONE = {
  urgent: AlertCircle,
  attention: Clock3,
  next: ArrowRight,
  steady: CheckCircle2,
};

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;
const guidePartnersRoom = getAdminRoomById('guide-partners');

function getFounding40Remaining(founding40Status) {
  if (!founding40Status) return null;
  return Math.max(0, (founding40Status.max_members || 0) - (founding40Status.current_count || 0));
}

function getTimeValue(value) {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCalendarDate(value) {
  const timestamp = getTimeValue(value);
  if (!timestamp) return 'Date pending';
  return new Date(timestamp).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getUpcomingEvents(siteSettings) {
  return getOperationalEvents(siteSettings?.upcoming_events);
}

function getPastSiteEventCount(siteSettings) {
  return splitSiteEvents(siteSettings?.upcoming_events).pastEvents.length;
}

function isGuestPassOpen(pass) {
  if (!pass || pass?.redeemed) return false;
  const expiresAt = getTimeValue(pass?.expires_at);
  if (!expiresAt) return true;
  return expiresAt >= Date.now();
}

function buildTodayFocusItems({
  stats,
  founding40Status,
  feedbackItems,
  analytics,
  atRiskMembers,
  guideSummary,
  siteSettings,
  pendingFocusReviewCount,
}) {
  const items = [];
  const remainingFounding40 = getFounding40Remaining(founding40Status);
  const postsToday = stats?.posts_today || 0;
  const feedbackCount = feedbackItems.length;
  const difficultFeedbackCount = feedbackItems.filter(
    (item) => item.sentiment === 'negative' || item.sentiment === 'mixed'
  ).length;
  const retentionRate = analytics?.retention?.retention_rate;
  const atRiskCount = atRiskMembers.length;
  const guideDerived = deriveGuidePartnersState(guideSummary);
  const hasZoomLink = !!siteSettings?.zoom_link;
  const upcomingEvents = getUpcomingEvents(siteSettings);
  const launchWindowActive = Date.now() <= LAUNCH_SPRINT_VISIBLE_UNTIL;

  if (atRiskCount > 0) {
    items.push({
      key: 'retention-risk',
      priority: 0,
      tone: 'urgent',
      eyebrow: 'Urgent today',
      title: `${pluralize(atRiskCount, 'dad')} may be drifting away`,
      detail: 'Review retention and churn signals before they harden.',
      roomId: 'steward-the-work',
      tab: 'analytics',
      actionLabel: 'Open Analytics',
    });
  }

  if (difficultFeedbackCount > 0) {
    items.push({
      key: 'feedback-risk',
      priority: 1,
      tone: 'attention',
      eyebrow: 'At risk',
      title: `${pluralize(difficultFeedbackCount, 'feedback item')} need closer listening`,
      detail: 'Open feedback and decide what needs to change first.',
      roomId: 'listen-learn',
      tab: 'feedback',
      actionLabel: 'Open Feedback',
    });
  } else if (feedbackCount > 0) {
    items.push({
      key: 'feedback-review',
      priority: 2,
      tone: 'next',
      eyebrow: 'Important next',
      title: `${pluralize(feedbackCount, 'feedback item')} ready to review`,
      detail: 'Look for patterns, not just isolated comments.',
      roomId: 'listen-learn',
      tab: 'feedback',
      actionLabel: 'Review Feedback',
    });
  }

  if (guideDerived.counts.onboardingCompleted > 0) {
    items.push({
      key: 'guide-partners-ready',
      priority: 1,
      tone: 'attention',
      eyebrow: 'Waiting on Kevin',
      title: `${pluralize(guideDerived.counts.onboardingCompleted, 'Guide Partner')} ready to schedule`,
      detail: 'These Guide Partners already completed onboarding and now need Kevin’s move back.',
      roomId: 'guide-partners',
      tab: 'guestguides',
      actionLabel: 'Open Guide Partners',
    });
  } else if (guideDerived.counts.onboardingSentOverdue > 0) {
    items.push({
      key: 'guide-partners-overdue',
      priority: 2,
      tone: 'attention',
      eyebrow: 'At risk',
      title: `${pluralize(guideDerived.counts.onboardingSentOverdue, 'Guide Partner')} overdue on onboarding`,
      detail: 'These handoffs are past the normal response window and need a fresh review.',
      roomId: 'guide-partners',
      tab: 'guestguides',
      actionLabel: 'Review Guide Partners',
    });
  }

  if (remainingFounding40 !== null && remainingFounding40 > 0) {
    items.push({
      key: 'founding-40',
      priority: 2,
      tone: 'next',
      eyebrow: 'Important next',
      title: `${pluralize(remainingFounding40, 'Founding 40 spot')} still open`,
      detail: 'Check who is warm and what should be sent next.',
      roomId: 'reach-dads',
      tab: 'members',
      actionLabel: 'Open Prospects',
    });
  }

  if (typeof retentionRate === 'number') {
    items.push({
      key: 'retention-rate',
      priority: retentionRate < 60 ? 1 : 3,
      tone: retentionRate < 60 ? 'attention' : 'next',
      eyebrow: retentionRate < 60 ? 'At risk' : 'Important next',
      title: `Retention is ${retentionRate}% right now`,
      detail: 'Use operations signals to see whether the mission is staying durable.',
      roomId: 'steward-the-work',
      tab: 'analytics',
      actionLabel: 'Review Retention',
    });
  }

  if (!hasZoomLink || upcomingEvents.length === 0) {
    items.push({
      key: 'launch-logistics',
      priority: !hasZoomLink ? 2 : 3,
      tone: 'attention',
      eyebrow: 'Current focus',
      title: !hasZoomLink ? 'Zoom link still needs a check' : 'Upcoming event still needs a check',
      detail: !hasZoomLink
        ? 'The event surface still needs a confirmed Zoom link in Site Settings.'
        : 'No upcoming event is configured yet in Site Settings.',
      roomId: 'system-health',
      tab: 'sitesettings',
      actionLabel: 'Open Site Settings',
    });
  }

  if (pendingFocusReviewCount > 0) {
    items.push({
      key: 'recording-review',
      priority: 4,
      tone: 'next',
      eyebrow: 'Review next',
      title: `${pluralize(pendingFocusReviewCount, 'recording draft')} ready for review`,
      detail: 'Support content is already close. Review it before it quietly stalls in the queue.',
      roomId: 'support-dads',
      tab: 'recordings',
      actionLabel: 'Open Recordings',
    });
  }

  if (postsToday > 0) {
    items.push({
      key: 'posts-today',
      priority: 3,
      tone: 'next',
      eyebrow: 'Important next',
      title: `${pluralize(postsToday, 'dad post')} came in today`,
      detail: 'Review what dads are asking for and what support should follow.',
      roomId: 'support-dads',
      tab: 'recordings',
      actionLabel: 'Open Recordings',
    });
  }

  const sorted = items.sort((a, b) => a.priority - b.priority).slice(0, 5);
  if (sorted.length > 0) return sorted;

  return [
    {
      key: 'orientation',
      priority: 9,
      tone: 'steady',
      eyebrow: 'Start here',
      title: 'The board is clear enough to work proactively',
      detail: 'Use the rooms below to decide what should move next.',
      roomId: 'reach-dads',
      tab: 'members',
      actionLabel: 'Open Prospects',
    },
  ];
}

function buildPatternItems({ feedbackItems, analytics, atRiskMembers, guideSummary, founding40Status }) {
  const items = [];
  const listenLearnContext = buildListenLearnRoomContext({
    room: { id: 'listen-learn' },
    feedbackItems,
    atRiskMembers,
  });
  const guideDerived = deriveGuidePartnersState(guideSummary);
  const retentionRate = analytics?.retention?.retention_rate;
  const remainingFounding40 = getFounding40Remaining(founding40Status);
  const guideSignals = [
    guideDerived.counts.onboardingCompleted > 0
      ? `${guideDerived.counts.onboardingCompleted} ready to schedule`
      : null,
    guideDerived.counts.onboardingSentOverdue > 0
      ? `${guideDerived.counts.onboardingSentOverdue} overdue`
      : null,
    guideDerived.counts.safeToInviteNow > 0
      ? `${guideDerived.counts.safeToInviteNow} safe to invite`
      : null,
  ].filter(Boolean);

  if (guideSignals.length > 0) {
    items.push({
      id: 'guide-partners-decision',
      tone: guideDerived.counts.onboardingCompleted > 0 || guideDerived.counts.onboardingSentOverdue > 0 ? 'attention' : 'next',
      label: 'Guide Partner readiness needs a choice',
      detail: guideSignals.join(' · '),
      decisionLabel: 'Decision to make',
      decisionPrompt: guideDerived.counts.onboardingCompleted > 0 && guideDerived.counts.safeToInviteNow > 0
        ? 'Decide whether this week should favor scheduling the ready guides first or refilling the next wave behind them.'
        : guideDerived.counts.onboardingCompleted > 0
          ? 'Decide whether to clear the scheduling-ready guides now before more handoffs pile up.'
          : 'Decide whether to restart the stalled handoffs or put more energy into fresh invitations.',
      source: 'In Guide Partners',
      countLabel: guideDerived.counts.onboardingCompleted > 0 ? `${guideDerived.counts.onboardingCompleted} waiting on Kevin` : 'Guide decision',
    });
  }

  listenLearnContext.patterns.slice(0, 2).forEach((pattern) => {
    items.push({
      id: `listen-${pattern.id}`,
      tone: pattern.tone,
      label: pattern.label,
      detail: `${pattern.count} in view${pattern.count > 0 ? ' right now' : ''}`,
      decisionLabel: 'Decision to make',
      decisionPrompt: pattern.tone === 'attention'
        ? 'Decide whether this signal needs a root-cause review before it hardens into a bigger issue.'
        : 'Decide whether this trend is strong enough to influence next week’s support or content choices.',
      source: pattern.route,
      countLabel: `${pattern.count} in view`,
    });
  });

  if (atRiskMembers.length > 0 || (typeof retentionRate === 'number' && retentionRate < 60)) {
    items.push({
      id: 'retention-watch',
      tone: atRiskMembers.length > 0 ? 'attention' : 'next',
      label: atRiskMembers.length > 0
        ? `${pluralize(atRiskMembers.length, 'dad')} at risk`
        : `Retention is ${retentionRate}%`,
      detail: atRiskMembers.length > 0
        ? 'Retention signals are stacking up right now, not just drifting in the background.'
        : 'Retention is soft enough that it may need a business-level decision before it worsens.',
      decisionLabel: 'Decision to make',
      decisionPrompt: atRiskMembers.length > 0
        ? 'Decide whether the next week should lean more toward rescue follow-up than growth work.'
        : 'Decide whether the current retention trend should change what gets protected this month.',
      source: 'In Operations',
      countLabel: typeof retentionRate === 'number' ? `Retention ${retentionRate}%` : 'Watch closely',
    });
  }

  if (remainingFounding40 !== null && remainingFounding40 > 0) {
    items.push({
      id: 'communication-pattern',
      tone: 'next',
      label: 'Founding 40 fill strategy is still open',
      detail: `${pluralize(remainingFounding40, 'Founding 40 spot')} still open right now.`,
      decisionLabel: 'Decision to make',
      decisionPrompt: 'Decide whether the remaining spots need broader reach, warmer follow-up, or a tighter close window.',
      source: 'In Prospects',
      countLabel: `${remainingFounding40} open`,
    });
  }

  return items.slice(0, 4);
}

function buildLaunchCriticalPathItems({
  founding40Status,
  guideSummary,
  siteSettings,
  pendingFocusReviewCount,
}) {
  const config = siteSettings?.launch_critical_path;
  if (config?.enabled === false) {
    return null;
  }

  const guideDerived = deriveGuidePartnersState(guideSummary);
  const remainingFounding40 = getFounding40Remaining(founding40Status);
  const hasZoomLink = !!siteSettings?.zoom_link;
  const upcomingEvents = getUpcomingEvents(siteSettings);
  const savedItems = Array.isArray(config?.items) ? config.items : [];
  const savedOverridesById = new Map(
    savedItems
      .filter((item) => (item?.source || 'manual') === 'auto' && item?.id)
      .map((item) => [item.id, item])
  );

  const autoCandidates = [];
  if (guideDerived.counts.onboardingCompleted > 0) {
    autoCandidates.push({
      id: 'guide-partners-ready',
      source: 'auto',
      tone: 'attention',
      sort_order: 1,
      title: `${pluralize(guideDerived.counts.onboardingCompleted, 'Guide Partner')} ready to schedule`,
      detail: 'Scheduling-ready Guide Partners are waiting on Kevin’s next move back.',
      actionLabel: 'Open Guide Partners',
      roomId: 'guide-partners',
      tab: 'guestguides',
    });
  }
  if (guideDerived.counts.onboardingSentOverdue > 0) {
    autoCandidates.push({
      id: 'guide-partners-overdue',
      source: 'auto',
      tone: 'attention',
      sort_order: 2,
      title: `${pluralize(guideDerived.counts.onboardingSentOverdue, 'Guide Partner')} overdue on onboarding`,
      detail: 'These onboarding handoffs are stalled long enough to matter right now.',
      actionLabel: 'Review Guide Partners',
      roomId: 'guide-partners',
      tab: 'guestguides',
    });
  }
  if (remainingFounding40 !== null && remainingFounding40 > 0) {
    autoCandidates.push({
      id: 'founding-40',
      source: 'auto',
      tone: 'next',
      sort_order: 3,
      title: `${pluralize(remainingFounding40, 'Founding 40 spot')} still open`,
      detail: 'Momentum still depends on filling the remaining Founding 40 seats.',
      actionLabel: 'Open Prospects',
      roomId: 'reach-dads',
      tab: 'members',
    });
  }
  if (!hasZoomLink || upcomingEvents.length === 0) {
    autoCandidates.push({
      id: 'launch-logistics',
      source: 'auto',
      tone: 'attention',
      sort_order: 4,
      title: !hasZoomLink ? 'Zoom link still needs a check' : 'Upcoming event still needs a check',
      detail: !hasZoomLink
        ? 'The event surface still needs a confirmed Zoom link in Site Settings.'
        : 'No upcoming event is configured yet in Site Settings.',
      actionLabel: 'Open Site Settings',
      roomId: 'system-health',
      tab: 'sitesettings',
    });
  }
  if (pendingFocusReviewCount > 0) {
    autoCandidates.push({
      id: 'recording-review',
      source: 'auto',
      tone: 'next',
      sort_order: 5,
      title: `${pluralize(pendingFocusReviewCount, 'recording draft')} ready for review`,
      detail: 'Support content is close enough that a focused push should not leave it waiting quietly.',
      actionLabel: 'Open Recordings',
      roomId: 'support-dads',
      tab: 'recordings',
    });
  }

  const mergedAutoItems = autoCandidates
    .map((candidate) => {
      const override = savedOverridesById.get(candidate.id);
      if (override?.enabled === false) {
        return null;
      }
      return {
        ...candidate,
        tone: override?.tone || candidate.tone,
        sort_order: Number.isFinite(override?.sort_order) ? override.sort_order : candidate.sort_order,
        title: override?.title_override || candidate.title,
        detail: override?.detail_override || candidate.detail,
        actionLabel: override?.action_label_override || candidate.actionLabel,
        roomId: override?.room_id || candidate.roomId,
        tab: override?.tab || candidate.tab,
      };
    })
    .filter(Boolean);

  const manualItems = savedItems
    .filter((item) => (item?.source || 'manual') === 'manual' && item?.enabled !== false)
    .map((item) => ({
      id: item.id,
      source: 'manual',
      tone: item.tone || 'next',
      sort_order: Number.isFinite(item.sort_order) ? item.sort_order : 999,
      title: item.title_override || '',
      detail: item.detail_override || '',
      actionLabel: item.action_label_override || 'Open now',
      roomId: item.room_id || '',
      tab: item.tab || '',
    }))
    .filter((item) => item.title && item.detail);

  const items = [...mergedAutoItems, ...manualItems]
    .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
    .slice(0, 5);

  if (!items.length) {
    return null;
  }

  return {
    label: config?.label || 'Current Focus',
    helperText: config?.helper_text || 'Keep the few highest-priority items in view above the daily queue.',
    items,
  };
}

function buildOwnershipLanes({ founding40Status, guideSummary, sentGuestPasses, pendingFocusReviewCount, siteSettings }) {
  const remainingFounding40 = getFounding40Remaining(founding40Status);
  const guideDerived = deriveGuidePartnersState(guideSummary);
  const openGuestPasses = (sentGuestPasses || []).filter(isGuestPassOpen);
  const upcomingEvents = getUpcomingEvents(siteSettings);

  const waitingOnKevin = [];
  if (guideDerived.counts.onboardingCompleted > 0) {
    waitingOnKevin.push({
      id: 'lane-ready-to-schedule',
      tone: 'attention',
      title: `Schedule ${pluralize(guideDerived.counts.onboardingCompleted, 'Guide Partner')}`,
      detail: 'They already completed onboarding, so the next move is Kevin reaching out personally.',
      roomId: 'guide-partners',
      tab: 'guestguides',
      actionLabel: 'Open Guide Partners',
    });
  }
  if (pendingFocusReviewCount > 0) {
    waitingOnKevin.push({
      id: 'lane-review-drafts',
      tone: 'attention',
      title: `Review ${pluralize(pendingFocusReviewCount, 'recording draft')}`,
      detail: 'Draft-ready support content is waiting for a human decision before it becomes useful to dads.',
      roomId: 'support-dads',
      tab: 'recordings',
      actionLabel: 'Open Recordings',
    });
  }
  if (remainingFounding40 > 0) {
    waitingOnKevin.push({
      id: 'lane-founding-outreach',
      tone: 'next',
      title: `Move the next ${pluralize(remainingFounding40, 'Founding 40 spot')}`,
      detail: 'Warm prospect outreach still needs Kevin’s next move while the cohort remains open.',
      roomId: 'reach-dads',
      tab: 'members',
      actionLabel: 'Open Prospects',
    });
  }

  const waitingOnThem = [];
  if (guideDerived.counts.onboardingSent > 0) {
    waitingOnThem.push({
      id: 'lane-onboarding-sent',
      tone: guideDerived.counts.onboardingSentOverdue > 0 ? 'attention' : 'next',
      title: `${pluralize(guideDerived.counts.onboardingSent, 'Guide Partner')} still need the form`,
      detail: guideDerived.counts.onboardingSentOverdue > 0
        ? `${pluralize(guideDerived.counts.onboardingSentOverdue, 'Guide Partner')} are already past the normal reminder window.`
        : 'These Guide Partners have the ball while onboarding is in their hands.',
      roomId: 'guide-partners',
      tab: 'guestguides',
      actionLabel: 'Review Guide Partners',
    });
  }
  if (openGuestPasses.length > 0) {
    waitingOnThem.push({
      id: 'lane-guest-passes-open',
      tone: 'next',
      title: `${pluralize(openGuestPasses.length, 'guest pass')} still open`,
      detail: 'These invitations are already out and are still waiting on a response from the other side.',
      roomId: 'reach-dads',
      tab: 'guestpasses',
      actionLabel: 'Open Guest Passes',
    });
  }

  const inMotion = [];
  if (guideDerived.counts.activeSequence > 0) {
    inMotion.push({
      id: 'lane-guide-sequence',
      tone: 'next',
      title: `${pluralize(guideDerived.counts.activeSequence, 'Guide Partner')} in active sequence`,
      detail: 'Invitation touches are already queued, so Kevin should avoid stepping on them without reason.',
      roomId: 'guide-partners',
      tab: 'guestguides',
      actionLabel: 'View Sequence Status',
    });
  }
  if (upcomingEvents.length > 0) {
    inMotion.push({
      id: 'lane-upcoming-events',
      tone: 'next',
      title: `${pluralize(upcomingEvents.length, 'upcoming event')} already scheduled`,
      detail: `Next up: ${upcomingEvents[0]?.title || 'Upcoming call'} on ${formatCalendarDate(upcomingEvents[0]?.start_datetime)}.`,
      roomId: 'system-health',
      tab: 'sitesettings',
      actionLabel: 'Open Events',
    });
  }

  return [
    {
      id: 'waiting-on-kevin',
      title: 'Waiting on Kevin',
      description: 'The ball is in your court. These are the items that need a human move now.',
      tone: waitingOnKevin.some((item) => item.tone === 'attention') ? 'attention' : waitingOnKevin.length ? 'next' : 'steady',
      items: waitingOnKevin.slice(0, 3),
      emptyState: 'Nothing obvious is waiting on Kevin right now.',
    },
    {
      id: 'waiting-on-them',
      title: 'Waiting on them',
      description: 'These handoffs are already out in the world and should be watched, not over-controlled.',
      tone: waitingOnThem.some((item) => item.tone === 'attention') ? 'attention' : waitingOnThem.length ? 'next' : 'steady',
      items: waitingOnThem.slice(0, 3),
      emptyState: 'No current handoff is obviously waiting on someone else.',
    },
    {
      id: 'in-motion',
      title: 'In motion',
      description: 'Work that is already moving so you can support it without creating noise.',
      tone: inMotion.length ? 'next' : 'steady',
      items: inMotion.slice(0, 3),
      emptyState: 'Nothing substantial is already in motion from the current data.',
    },
  ];
}

function buildZoneSummary(zone, { stats, founding40Status, feedbackItems, analytics, atRiskMembers, guideSummary, supportOverview, advisoryOverview, superFanOverview }) {
  const remainingFounding40 = getFounding40Remaining(founding40Status);
  const postsToday = stats?.posts_today || 0;
  const totalRecordings = stats?.total_recordings || 0;
  const feedbackCount = feedbackItems.length;
  const difficultFeedbackCount = feedbackItems.filter(
    (item) => item.sentiment === 'negative' || item.sentiment === 'mixed'
  ).length;
  const retentionRate = analytics?.retention?.retention_rate;
  const atRiskCount = atRiskMembers.length;
  const guideStatusCounts = guideSummary?.statusCounts || {};
  const guideInviteStageCounts = guideSummary?.inviteStageCounts || {};
  const guideScheduled = guideStatusCounts.scheduled || 0;
  const guideActive = guideStatusCounts.active || 0;
  const guideNotInvited = guideInviteStageCounts.not_invited || 0;
  const guideInvited = guideInviteStageCounts.invited || 0;
  const guideOnboardingSent = guideInviteStageCounts.onboarding_sent || 0;
  const guideOnboardingCompleted = guideInviteStageCounts.onboarding_completed || 0;
  const guideMovingForward = guideOnboardingSent + guideOnboardingCompleted + guideScheduled + guideActive;
  const guideDerived = deriveGuidePartnersState(guideSummary);
  const advisoryStats = advisoryOverview?.stats || {};
  const advisoryTotalAccounts = Number(advisoryStats.total_accounts || 0);
  const advisoryLayeredAccounts = Number(advisoryStats.additional_role_accounts || 0);
  const advisoryInactiveAccounts = Number(advisoryStats.inactive_accounts || 0);
  const supportStats = supportOverview?.stats || {};
  const failedSupportSyncs = Number(supportStats.failed_syncs || 0);
  const totalSupportPartners = Number(supportStats.total_supporters || 0);
  const unsubscribedSupporters = Number(supportStats.unsubscribed || 0);
  const superFanStats = superFanOverview?.stats || {};
  const totalSuperFanAccounts = Number(superFanStats.total_accounts || 0);
  const layeredSuperFanAccounts = Number(superFanStats.additional_role_accounts || 0);
  const inactiveSuperFanAccounts = Number(superFanStats.inactive_accounts || 0);

  switch (zone.id) {
    case 'marketing':
      return {
        statusTone: 'next',
        statusLabel: 'Active',
        signals: [
          { tone: 'next', text: 'Content queue is live — track every piece from idea to distributed.' },
          { tone: 'steady', text: 'Use Content Research to pull live Reddit topics directly into your queue.' },
        ],
      };
    case 'free-users':
      return {
        statusTone: 'steady',
        statusLabel: 'Coming soon',
        signals: [
          { tone: 'steady', text: 'Activation, nurture, and free-user conversion work will surface here once the product flow is defined.' },
          { tone: 'steady', text: 'This lane is intentionally visible now, but no backend workflow is wired yet.' },
        ],
      };
    case 'communication':
      return {
        statusTone: remainingFounding40 > 0 ? 'attention' : 'next',
        statusLabel: remainingFounding40 > 0 ? 'Needs attention' : 'Ready',
        signals: remainingFounding40 !== null
          ? [
            { tone: remainingFounding40 > 0 ? 'attention' : 'steady', text: `${pluralize(remainingFounding40, 'Founding 40 spot')} still open` },
            { tone: 'next', text: 'Prospects, guest passes, and early follow-up live here.' },
          ]
          : [
            { tone: 'next', text: 'Start by reviewing warm prospects and sending the next guest pass.' },
            { tone: 'steady', text: 'Prospects, referrals, and guest passes are grouped here.' },
          ],
      };
    case 'heart-work':
      return {
        statusTone: difficultFeedbackCount > 0 ? 'attention' : postsToday > 0 || atRiskCount > 0 ? 'next' : 'steady',
        statusLabel: difficultFeedbackCount > 0 ? 'Needs attention' : postsToday > 0 || atRiskCount > 0 ? 'Active today' : 'Build next',
        signals: [
          {
            tone: difficultFeedbackCount > 0 ? 'attention' : feedbackCount > 0 ? 'next' : 'steady',
            text: feedbackCount > 0
              ? `${pluralize(feedbackCount, 'feedback item')} in view`
              : 'Dad-care and support signals will surface here.',
          },
          {
            tone: postsToday > 0 || totalRecordings > 0 ? 'next' : 'steady',
            text: postsToday > 0
              ? `${pluralize(postsToday, 'dad post')} today and ${pluralize(totalRecordings, 'recording')} in the library`
              : `${pluralize(totalRecordings, 'recording')} in the library`,
          },
        ],
      };
    case 'guide-partners':
      return {
        statusTone: guideDerived.counts.onboardingCompleted > 0 || guideDerived.counts.onboardingSentOverdue > 0
          ? 'attention'
          : guideDerived.counts.safeToInviteNow > 0
            ? 'next'
            : guideMovingForward > 0
              ? 'next'
              : guideNotInvited > 0 || guideInvited > 0
                ? 'attention'
                : 'steady',
        statusLabel: guideDerived.counts.onboardingCompleted > 0
          ? 'Needs Kevin'
          : guideDerived.counts.onboardingSentOverdue > 0
            ? 'Needs attention'
            : guideDerived.counts.safeToInviteNow > 0
              ? 'Ready now'
              : guideMovingForward > 0
                ? 'In motion'
                : guideNotInvited > 0 || guideInvited > 0
                  ? 'Needs attention'
                  : 'Build next',
        signals: [
          {
            tone: guideDerived.counts.onboardingCompleted > 0 ? 'attention' : guideDerived.counts.safeToInviteNow > 0 ? 'next' : 'steady',
            text: guideDerived.counts.onboardingCompleted > 0
              ? `${pluralize(guideDerived.counts.onboardingCompleted, 'Guide Partner')} ready to schedule right now`
              : guideDerived.counts.safeToInviteNow > 0
                ? `${pluralize(guideDerived.counts.safeToInviteNow, 'Guide Partner')} safe to invite right now`
                : guidePartnersRoom?.emptyPrompt || 'Start by reviewing who could share, coach, or serve again.',
          },
          {
            tone: guideDerived.counts.onboardingSentOverdue > 0 ? 'attention' : guideDerived.counts.activeSequence > 0 ? 'attention' : guideMovingForward > 0 ? 'next' : 'steady',
            text: guideDerived.counts.onboardingSentOverdue > 0
              ? `${pluralize(guideDerived.counts.onboardingSentOverdue, 'Guide Partner')} overdue on onboarding`
              : guideDerived.counts.activeSequence > 0
                ? `${pluralize(guideDerived.counts.activeSequence, 'Guide Partner')} already sitting in the queued invite sequence`
                : guideMovingForward > 0
                  ? `${pluralize(guideMovingForward, 'Guide Partner')} moving through onboarding or service`
                  : 'The current Guide Partners workflow is the starting foundation here.',
          },
        ],
      };
    case 'advisory-partners':
      return {
        statusTone: advisoryInactiveAccounts > 0 ? 'attention' : advisoryLayeredAccounts > 0 || advisoryTotalAccounts > 0 ? 'next' : 'steady',
        statusLabel: advisoryInactiveAccounts > 0 ? 'Needs review' : advisoryLayeredAccounts > 0 ? 'Clarify access' : advisoryTotalAccounts > 0 ? 'In motion' : 'Build next',
        signals: [
          {
            tone: advisoryTotalAccounts > 0 ? 'next' : 'steady',
            text: advisoryTotalAccounts > 0
              ? `${pluralize(advisoryTotalAccounts, 'advisory account')} currently have access`
              : 'Advisory access and private asks will surface here.',
          },
          {
            tone: advisoryInactiveAccounts > 0 ? 'attention' : advisoryLayeredAccounts > 0 ? 'next' : 'steady',
            text: advisoryInactiveAccounts > 0
              ? `${pluralize(advisoryInactiveAccounts, 'account')} may no longer be active`
              : advisoryLayeredAccounts > 0
                ? `${pluralize(advisoryLayeredAccounts, 'account')} still carry layered advisory access`
                : 'No advisory access drift is currently surfaced.',
          },
        ],
      };
    case 'stewardship':
      return {
        statusTone: atRiskCount > 0 ? 'attention' : 'next',
        statusLabel: atRiskCount > 0 ? 'Needs attention' : 'Steady',
        signals: [
          {
            tone: typeof retentionRate === 'number' ? 'next' : 'steady',
            text: typeof retentionRate === 'number' ? `Retention is ${retentionRate}%` : 'Retention signal will surface here.',
          },
          {
            tone: atRiskCount > 0 ? 'attention' : 'steady',
            text: atRiskCount > 0
              ? `${pluralize(atRiskCount, 'dad')} flagged for churn risk`
              : 'Pricing, documents, settings, and site upkeep live here.',
          },
        ],
      };
    case 'support-partners':
      return {
        statusTone: failedSupportSyncs > 0 ? 'attention' : totalSupportPartners > 0 || unsubscribedSupporters > 0 ? 'next' : 'steady',
        statusLabel: failedSupportSyncs > 0 ? 'Needs attention' : totalSupportPartners > 0 || unsubscribedSupporters > 0 ? 'In motion' : 'Build next',
        signals: [
          {
            tone: totalSupportPartners > 0 ? 'next' : 'steady',
            text: totalSupportPartners > 0
              ? `${pluralize(totalSupportPartners, 'supporter')} currently visible in mission support`
              : 'Support-partner accounts and mission support records will surface here.',
          },
          {
            tone: failedSupportSyncs > 0 ? 'attention' : unsubscribedSupporters > 0 ? 'next' : 'steady',
            text: failedSupportSyncs > 0
              ? `${pluralize(failedSupportSyncs, 'support sync')} currently need review`
              : unsubscribedSupporters > 0
                ? `${pluralize(unsubscribedSupporters, 'supporter')} unsubscribed from updates`
                : 'Support trust and sync health are currently quiet.',
          },
        ],
      };
    case 'super-fans':
      return {
        statusTone: inactiveSuperFanAccounts > 0 ? 'attention' : totalSuperFanAccounts > 0 || layeredSuperFanAccounts > 0 ? 'next' : 'steady',
        statusLabel: inactiveSuperFanAccounts > 0 ? 'Needs review' : totalSuperFanAccounts > 0 ? 'In motion' : 'Build next',
        signals: [
          {
            tone: totalSuperFanAccounts > 0 ? 'next' : 'steady',
            text: totalSuperFanAccounts > 0
              ? `${pluralize(totalSuperFanAccounts, 'super fan account')} currently have access`
              : 'Super-fan access will surface here.',
          },
          {
            tone: inactiveSuperFanAccounts > 0 ? 'attention' : layeredSuperFanAccounts > 0 ? 'next' : 'steady',
            text: inactiveSuperFanAccounts > 0
              ? `${pluralize(inactiveSuperFanAccounts, 'account')} may no longer be active`
              : layeredSuperFanAccounts > 0
                ? `${pluralize(layeredSuperFanAccounts, 'account')} still carry layered super-fan access`
                : 'No super-fan access drift is currently surfaced.',
          },
        ],
      };
    default:
      return {
        statusTone: 'steady',
        statusLabel: 'Ready',
        signals: [{ tone: 'steady', text: 'Choose the zone that matches the work you need to do next.' }],
      };
  }
}

function DoFirstHeroCard({ item, onOpen }) {
  if (!item) return null;

  return (
    <button
      type="button"
      onClick={() => onOpen(item.roomId, item.tab)}
      className={`relative overflow-hidden rounded-[1.75rem] border p-6 text-left shadow-xl transition-all hover:-translate-y-0.5 hover:border-aqua/40 hover:shadow-2xl ${FOCUS_TONE_CLASSES[item.tone] || FOCUS_TONE_CLASSES.steady}`}
    >
      <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${FOCUS_EDGE_CLASSES[item.tone] || FOCUS_EDGE_CLASSES.steady}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border text-sm font-semibold ${FOCUS_NUMBER_CLASSES[item.tone] || FOCUS_NUMBER_CLASSES.steady}`}>
            1
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-500 dark:text-gray-400">
              Do first
            </p>
            <h3 className="mt-3 text-xl font-semibold leading-8 text-slate-900 dark:text-white">
              {item.title}
            </h3>
          </div>
        </div>
        <Badge className={FOCUS_BADGE_CLASSES[item.tone] || FOCUS_BADGE_CLASSES.steady}>
          {item.eyebrow}
        </Badge>
      </div>
      <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-gray-300">
        {item.detail}
      </p>
      <div className="mt-5 flex items-center justify-between border-t border-current/10 pt-4">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-gray-400">
          {item.actionLabel || 'Open now'}
        </span>
        <div className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-gray-200">
          <span>Go</span>
          <ArrowRight className="h-4 w-4" />
        </div>
      </div>
    </button>
  );
}

function CompactPriorityQueue({ items = [], onOpen }) {
  if (!items.length) return null;

  return (
    <Card className="rounded-[1.35rem] border-slate-200 bg-white/90 p-4 shadow-md dark:border-white/10 dark:bg-white/[0.03]">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-500 dark:text-gray-400">
            Then
          </p>
          <p className="mt-1.5 text-sm text-slate-600 dark:text-gray-300">
            The next few moves stay visible so the day keeps its shape after the first action.
          </p>
        </div>
        <Badge className="border-slate-200 bg-slate-50 text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-gray-300">
          {items.length} next
        </Badge>
      </div>
      <div className="mt-3 space-y-2">
        {items.map((item, index) => (
          <button
            key={item.key}
            type="button"
            onClick={() => onOpen(item.roomId, item.tab)}
            className="flex w-full items-start gap-3 rounded-[1rem] border border-slate-200 bg-slate-50/75 px-3 py-2.5 text-left transition-colors hover:border-aqua/40 hover:bg-white dark:border-white/10 dark:bg-black/10 dark:hover:bg-black/20"
          >
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border text-sm font-semibold ${FOCUS_NUMBER_CLASSES[item.tone] || FOCUS_NUMBER_CLASSES.steady}`}>
              {index + 2}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold leading-6 text-slate-900 dark:text-white">
                  {item.title}
                </p>
                <Badge className={STATUS_BADGE_CLASSES[item.tone] || STATUS_BADGE_CLASSES.steady}>
                  {item.eyebrow}
                </Badge>
              </div>
              <p className="mt-1 text-[13px] leading-5 text-slate-600 dark:text-gray-300">
                {item.detail}
              </p>
              <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-400">
                {item.actionLabel}
              </p>
            </div>
            <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-400 dark:text-gray-400" />
          </button>
        ))}
      </div>
    </Card>
  );
}

function TopStatusStrip({ founding40Status, siteSettings, guideSummary }) {
  const remainingFounding40 = getFounding40Remaining(founding40Status);
  const upcomingEvents = getUpcomingEvents(siteSettings);
  const pastEventCount = getPastSiteEventCount(siteSettings);
  const nextEvent = upcomingEvents[0];
  const guideDerived = deriveGuidePartnersState(guideSummary);
  const items = [
    founding40Status
      ? {
        key: 'founding-40',
        icon: Crown,
        label: remainingFounding40 > 0
          ? `${pluralize(remainingFounding40, 'spot')} still open`
          : 'Founding 40 currently full',
      }
      : null,
    nextEvent
      ? {
        key: 'next-event',
        icon: Clock3,
        label: `Next event: ${nextEvent.title || 'Upcoming call'} on ${formatCalendarDate(nextEvent.start_datetime)}`,
      }
      : null,
    pastEventCount > 0
      ? {
        key: 'past-event-warning',
        icon: AlertCircle,
        label: `${pluralize(pastEventCount, 'past event')} still in Site Settings`,
      }
      : null,
    guideDerived.counts.onboardingCompleted > 0
      ? {
        key: 'guide-ready',
        icon: CheckCircle2,
        label: `${pluralize(guideDerived.counts.onboardingCompleted, 'Guide Partner')} ready for Kevin`,
      }
      : null,
  ].filter(Boolean).slice(0, 4);

  if (!items.length) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <div
            key={item.key}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/75 px-2.5 py-1.5 text-[13px] text-slate-700 dark:border-white/10 dark:bg-black/15 dark:text-gray-300"
          >
            <Icon className="h-3.5 w-3.5 text-slate-500 dark:text-gray-400" />
            <span>{item.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function CriticalPathBanner({ banner, onOpen }) {
  if (!banner?.items?.length) return null;

  return (
    <Card className="mt-5 overflow-hidden border-amber-200/80 bg-gradient-to-r from-amber-50 via-white to-cyan-50/60 p-4 shadow-md dark:border-amber-400/15 dark:from-[#211913] dark:via-[#161518] dark:to-[#11181c]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-700 dark:border-amber-400/20 dark:bg-black/15 dark:text-amber-300">
            <Crown className="h-3.5 w-3.5" />
            {banner.label}
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
            {banner.helperText}
          </p>
        </div>
        <Badge className="border-slate-200 bg-white text-slate-700 dark:border-white/10 dark:bg-black/15 dark:text-gray-300">
          {banner.items.length} must land
        </Badge>
      </div>

      <div className="mt-4 grid gap-3 xl:grid-cols-3">
        {banner.items.map((item) => {
          const Icon = SIGNAL_ICON_BY_TONE[item.tone] || ArrowRight;
          const isActionable = !!item.roomId && !!item.tab;
          const Wrapper = isActionable ? 'button' : 'div';
          return (
            <Wrapper
              key={item.id}
              type={isActionable ? 'button' : undefined}
              onClick={isActionable ? () => onOpen(item.roomId, item.tab) : undefined}
              className={`relative overflow-hidden rounded-[1.1rem] border p-4 text-left transition-all ${CRITICAL_PATH_PANEL_CLASSES[item.tone] || CRITICAL_PATH_PANEL_CLASSES.next} ${isActionable ? 'hover:-translate-y-0.5 hover:border-[#90b7d0]/70' : ''}`}
              style={STRATEGIC_TEXTURE_STYLES.strong}
            >
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.07),transparent_35%,rgba(0,0,0,0.1))]" />
              <div className="absolute inset-x-0 top-0 h-px bg-white/20" />
              <div className="relative z-10 flex items-start justify-between gap-3">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${CRITICAL_PATH_ICON_TONE_CLASSES[item.tone] || CRITICAL_PATH_ICON_TONE_CLASSES.next}`}>
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <Badge className={STRATEGIC_BADGE_CLASSES[item.tone] || STRATEGIC_BADGE_CLASSES.steady}>
                  {item.source === 'manual' ? 'Manual' : 'Auto'}
                </Badge>
              </div>
              <h3 className="relative z-10 mt-4 text-base font-semibold leading-6 text-[#f7f1e7]">
                {item.title}
              </h3>
              <p className="relative z-10 mt-2 text-sm leading-6 text-[#f1e8da]/88">
                {item.detail}
              </p>
              {isActionable ? (
                <div className="relative z-10 mt-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#d8e7ee]">
                  <span>{item.actionLabel || 'Open now'}</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </div>
              ) : null}
            </Wrapper>
          );
        })}
      </div>
    </Card>
  );
}

function PatternInsightCard({ item }) {
  return (
    <Card
      className={`relative overflow-hidden rounded-[1.2rem] border p-4 shadow-sm ${PATTERN_CARD_CLASSES[item.tone] || PATTERN_CARD_CLASSES.steady}`}
      style={STRATEGIC_TEXTURE_STYLES.soft}
    >
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),transparent_38%,rgba(0,0,0,0.08))]" />
      <div className="absolute inset-x-0 top-0 h-px bg-white/16" />
      <div className="relative z-10 flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#d3e3ea]/72">
            Bigger decision
          </p>
          <h4 className="mt-2 text-base font-semibold leading-6 text-[#f7f1e7]">
            {item.label}
          </h4>
        </div>
        <Badge className={STRATEGIC_BADGE_CLASSES[item.tone] || STRATEGIC_BADGE_CLASSES.steady}>
          {item.countLabel}
        </Badge>
      </div>
      <p className="relative z-10 mt-2.5 text-sm leading-5 text-[#efe5d8]/86">
        {item.detail}
      </p>
      {item.decisionPrompt ? (
        <div className="relative z-10 mt-3 rounded-[0.95rem] border border-white/12 bg-black/20 px-3 py-3 backdrop-blur-[1px]">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#d3e3ea]/72">
            {item.decisionLabel || 'Decision to make'}
          </p>
          <p className="mt-1.5 text-sm leading-5 text-[#f3ebdf]">
            {item.decisionPrompt}
          </p>
        </div>
      ) : null}
      <div className="relative z-10 mt-2.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#d3e3ea]/72">
        <Waves className="h-4 w-4" />
        <span>{item.source}</span>
      </div>
    </Card>
  );
}

function OwnershipLane({ lane, onOpen }) {
  return (
    <Card className={`rounded-[1.5rem] border p-5 shadow-lg ${LANE_PANEL_CLASSES[lane.tone] || LANE_PANEL_CLASSES.steady}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-lg font-semibold text-slate-900 dark:text-white">
            {lane.title}
          </h4>
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
            {lane.description}
          </p>
        </div>
        <Badge className={STATUS_BADGE_CLASSES[lane.tone] || STATUS_BADGE_CLASSES.steady}>
          {lane.items.length ? pluralize(lane.items.length, 'item') : 'Clear'}
        </Badge>
      </div>

      <div className="mt-5 space-y-3">
        {lane.items.length ? lane.items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onOpen(item.roomId, item.tab)}
            className={`w-full rounded-[1.1rem] border p-4 text-left transition-colors hover:border-aqua/40 ${LANE_ITEM_CLASSES[item.tone] || LANE_ITEM_CLASSES.steady}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-base font-semibold text-slate-900 dark:text-white">
                  {item.title}
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
                  {item.detail}
                </p>
              </div>
              <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-400 dark:text-gray-400" />
            </div>
          </button>
        )) : (
          <div className="rounded-[1.1rem] border border-dashed border-slate-200 bg-white/70 px-4 py-6 text-sm leading-6 text-slate-600 dark:border-white/10 dark:bg-black/10 dark:text-gray-300">
            {lane.emptyState}
          </div>
        )}
      </div>
    </Card>
  );
}

function RecentMovementItem({ item }) {
  const typeLabel = item?.type ? String(item.type).replace('_', ' ') : 'activity';

  return (
    <div className="rounded-[1.1rem] border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.03]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Badge className="border-slate-200 bg-slate-50 text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-gray-300">
            {typeLabel}
          </Badge>
          <p className="mt-3 text-base font-semibold text-slate-900 dark:text-white">
            {item.action}
          </p>
          <p className="mt-1 text-sm text-slate-600 dark:text-gray-300">
            {item.user}{item.detail ? ` • ${item.detail}` : ''}
          </p>
          {item.content_preview ? (
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-400">
              {item.content_preview}
            </p>
          ) : null}
        </div>
        <p className="shrink-0 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-400">
          {formatTimeAgo(item.time)}
        </p>
      </div>
    </div>
  );
}

function RoomSignal({ signal }) {
  const Icon = SIGNAL_ICON_BY_TONE[signal.tone] || MinusCircle;

  return (
    <div className="flex items-start gap-3 rounded-xl bg-slate-50 px-3 py-2.5 dark:bg-secondary">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400 dark:text-gray-400" />
      <p className="text-sm text-slate-700 dark:text-gray-300">{signal.text}</p>
    </div>
  );
}

function ZoneCard({ zone, summary, onOpenZone, onOpenTool }) {
  const accent = ZONE_ACCENTS[zone.id] || ZONE_ACCENTS['heart-work'];
  const Icon = zone.icon;

  return (
    <Card className={`relative flex h-full flex-col overflow-hidden border-slate-200 bg-white p-6 shadow-xl dark:border-border dark:bg-card ${accent.panel}`}>
      <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${accent.topBar}`} />
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className={`text-[11px] font-semibold uppercase tracking-[0.28em] ${accent.eyebrow}`}>Operating lane</p>
          <div className="mt-3 flex items-center gap-3">
            <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${accent.icon}`}>
              <Icon className="h-5 w-5" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">{zone.title}</h3>
          </div>
        </div>
        <Badge className={STATUS_BADGE_CLASSES[summary.statusTone] || STATUS_BADGE_CLASSES.steady}>
          {summary.statusLabel}
        </Badge>
      </div>

      <div className={`mt-5 rounded-[1.4rem] border p-4 ${accent.questionPanel}`}>
        <p className={`text-[11px] font-semibold uppercase tracking-[0.24em] ${accent.eyebrow}`}>Question</p>
        <p className="mt-3 text-lg font-semibold leading-7 text-slate-900 dark:text-white">{zone.question}</p>
      </div>

      <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-gray-400">{zone.supportingLine}</p>

      <div className="mt-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-400">Watching now</p>
      </div>

      <div className="mt-3 space-y-2.5">
        {summary.signals.slice(0, 2).map((signal, index) => (
          <RoomSignal key={`${zone.id}-signal-${index}`} signal={signal} />
        ))}
      </div>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-slate-200/80 pt-5 dark:border-white/10">
        <Button
          type="button"
          onClick={() => onOpenZone(zone.id)}
          className="bg-aqua text-charcoal hover:bg-aqua-dark"
        >
          Open Lane
        </Button>
        {zone.tools.slice(0, 2).map((tool) => (
          <button
            key={`${zone.id}-${tool.tab}`}
            type="button"
            onClick={() => onOpenTool(tool.roomId || zone.id, tool.tab)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 transition-colors hover:border-aqua/40 hover:text-slate-900 dark:border-border dark:bg-secondary dark:text-gray-300 dark:hover:border-aqua/30 dark:hover:text-white"
          >
            <tool.icon className="h-4 w-4" />
            {tool.label}
          </button>
        ))}
      </div>
    </Card>
  );
}

export default function CommandCenterLanding({
  zones,
  stats,
  activity,
  loading,
  notAuthorized,
  founding40Status,
  analytics,
  atRiskMembers,
  feedbackItems,
  guideSummary,
  advisoryOverview,
  supportOverview,
  superFanOverview,
  sentGuestPasses,
  siteSettings,
  pendingFocusReviewCount,
  commandCenterQueue,
  commandCenterQueueLoading,
  commandCenterQueueRefreshing,
  commandCenterQueueError,
  commandCenterQueueCreating,
  commandCenterQueueMutatingIds,
  onRefreshQueue,
  onCreateQueueItem,
  onUpdateQueueItem,
  onResolveQueueItem,
  onDeferQueueItem,
  onReviewQueueItem,
  onDismissQueueItem,
  onDeleteQueueItem,
  onOpenZone,
  onOpenTool,
}) {
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [editingQueueItem, setEditingQueueItem] = useState(null);
  const [recentMovementExpanded, setRecentMovementExpanded] = useState(false);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-aqua" />
      </div>
    );
  }

  if (notAuthorized) {
    return (
      <Card className="border-slate-200 bg-white p-6 dark:border-charcoal-lighter dark:bg-charcoal-light">
        <h3 className="mb-2 font-semibold text-slate-900 dark:text-white">Not authorized</h3>
        <p className="text-sm text-slate-600 dark:text-gray-400">
          Your account does not currently have access to admin data.
        </p>
      </Card>
    );
  }

  const ownershipLanes = buildOwnershipLanes({
    founding40Status,
    guideSummary,
    sentGuestPasses,
    pendingFocusReviewCount,
    siteSettings,
  });
  const patternItems = buildPatternItems({
    feedbackItems,
    analytics,
    atRiskMembers,
    guideSummary,
    founding40Status,
  });
  const launchCriticalPathBanner = buildLaunchCriticalPathItems({
    founding40Status,
    guideSummary,
    siteSettings,
    pendingFocusReviewCount,
  });
  const handleOpenQueueItem = (item) => {
    const target = getQueueItemNavigation(item);
    if (!target) {
      toast.info('This queue item does not have an exact destination yet.');
      return;
    }
    onOpenTool?.(target.roomId, target.tab, target.toolContext);
  };

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden border-slate-200 bg-gradient-to-br from-white via-slate-50 to-secondary p-6 dark:border-border dark:bg-gradient-to-br dark:from-card dark:via-secondary dark:to-background">
        <div className="max-w-5xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-aqua/80">
            Dad Partners Command Center
          </p>
          <h2 className="mt-3 text-3xl font-bold text-slate-900 dark:text-white">Do the next right thing.</h2>
          <p className="mt-2.5 max-w-3xl text-base leading-7 text-slate-600 dark:text-gray-300">
            Start with the clearest first move, then keep the rest of the queue in view without turning the page into a dashboard.
          </p>

          <div className="mt-5">
            <TopStatusStrip
              founding40Status={founding40Status}
              siteSettings={siteSettings}
              guideSummary={guideSummary}
            />
          </div>

          <CriticalPathBanner banner={launchCriticalPathBanner} onOpen={onOpenTool} />
        </div>
      </Card>

      <Tabs defaultValue="priority">
        <TabsList>
          <TabsTrigger value="priority">By Priority</TabsTrigger>
          <TabsTrigger value="category">By Category</TabsTrigger>
        </TabsList>
        <TabsContent value="priority" className="mt-4 space-y-6">
          <ActionListSection
            activeItems={Array.isArray(commandCenterQueue?.active) ? commandCenterQueue.active : []}
            needsReviewItems={Array.isArray(commandCenterQueue?.needs_review) ? commandCenterQueue.needs_review : []}
            loading={commandCenterQueueLoading}
            refreshing={commandCenterQueueRefreshing}
            error={commandCenterQueueError}
            creating={commandCenterQueueCreating}
            mutatingIds={commandCenterQueueMutatingIds || {}}
            onRefresh={onRefreshQueue}
            onOpenQuickAdd={() => setQuickAddOpen(true)}
            onOpenItem={handleOpenQueueItem}
            onEditItem={(item) => setEditingQueueItem(item)}
            onResolveItem={(item) => onResolveQueueItem?.(item?.queue_item_id)}
            onDeferItem={(item) => onDeferQueueItem?.(item?.queue_item_id)}
            onPromoteReviewItem={(item) => onReviewQueueItem?.(item?.queue_item_id, { state: 'active' })}
            onSendReviewItemLater={(item) => onReviewQueueItem?.(item?.queue_item_id, { state: 'later' })}
            onDismissReviewItem={(item) => onDismissQueueItem?.(item?.queue_item_id)}
          />
          <LaterSection
            items={Array.isArray(commandCenterQueue?.later) ? commandCenterQueue.later : []}
            onOpenItem={handleOpenQueueItem}
            onEditItem={(item) => setEditingQueueItem(item)}
          />
        </TabsContent>
        <TabsContent value="category" className="mt-4">
          <QueueCategorySection
            queueState={commandCenterQueue}
            onOpenItem={handleOpenQueueItem}
            onEditItem={(item) => setEditingQueueItem(item)}
          />
        </TabsContent>
      </Tabs>

      <ResolvedTodaySection
        items={Array.isArray(commandCenterQueue?.resolved_today) ? commandCenterQueue.resolved_today : []}
      />

      <QuickAddActionDialog
        open={quickAddOpen}
        creating={commandCenterQueueCreating}
        onOpenChange={setQuickAddOpen}
        onSubmit={onCreateQueueItem}
      />

      <QueueItemEditDialog
        item={editingQueueItem}
        open={Boolean(editingQueueItem)}
        saving={Boolean(editingQueueItem?.queue_item_id && commandCenterQueueMutatingIds?.[editingQueueItem.queue_item_id])}
        onOpenChange={(open) => {
          if (!open) setEditingQueueItem(null);
        }}
        onSubmit={onUpdateQueueItem}
        onDelete={onDeleteQueueItem}
        onResolve={onResolveQueueItem}
        onDefer={onDeferQueueItem}
        onPromoteToActive={(queueItemId) => onReviewQueueItem?.(queueItemId, { state: 'active' })}
        onSendToLater={(queueItemId) => onReviewQueueItem?.(queueItemId, { state: 'later' })}
        onDismiss={onDismissQueueItem}
      />

      <CollapsibleSection
        title="Ownership Lanes"
        tooltip="Shows whose move it is next — waiting on Kevin, waiting on them, or already in motion — so you know who holds the ball, not just what's on the list."
        badge={(
          <Badge className="border-slate-200 bg-white text-slate-700 dark:border-border dark:bg-secondary dark:text-gray-300">
            3 lanes
          </Badge>
        )}
      >
        <div className="grid gap-4 xl:grid-cols-3">
          {ownershipLanes.map((lane) => (
            <OwnershipLane key={lane.id} lane={lane} onOpen={onOpenTool} />
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Bigger Decisions Needed"
        tooltip="Not tasks — judgment calls. Signals that a founder-level decision may be forming, before it's urgent enough to demand one."
        badge={(
          <Badge className="border-slate-200 bg-white text-slate-700 dark:border-border dark:bg-secondary dark:text-gray-300">
            {patternItems.length} {patternItems.length === 1 ? 'decision' : 'decisions'}
          </Badge>
        )}
      >
        <div className="grid gap-3 xl:grid-cols-2">
          {patternItems.map((item) => (
            <PatternInsightCard key={item.id} item={item} />
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Operating Lanes"
        tooltip="One glance across all 10 lanes to catch anything on fire, without leaving this page. Click a lane to open its full tab."
        badge={(
          <Badge className="border-slate-200 bg-white text-slate-700 dark:border-border dark:bg-secondary dark:text-gray-300">
            {zones.length} {zones.length === 1 ? 'lane' : 'lanes'}
          </Badge>
        )}
      >
        <div className="grid gap-4 xl:grid-cols-2">
          {zones.map((zone) => (
            <ZoneCard
              key={zone.id}
              zone={zone}
              summary={buildZoneSummary(zone, {
                stats,
                founding40Status,
                feedbackItems,
                analytics,
                atRiskMembers,
                guideSummary,
                advisoryOverview,
                supportOverview,
                superFanOverview,
              })}
              onOpenZone={onOpenZone}
              onOpenTool={onOpenTool}
            />
          ))}
        </div>
      </CollapsibleSection>

      <div>
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setRecentMovementExpanded(!recentMovementExpanded)}
              className="h-8 w-8 p-0 text-slate-500 hover:bg-slate-100 dark:text-gray-400 dark:hover:bg-white/[0.05]"
            >
              {recentMovementExpanded ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </Button>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Recent Movement</h3>
              <p className="text-sm text-slate-600 dark:text-gray-400">A live feed of what has actually moved so the day has context, not just priorities.</p>
            </div>
          </div>
          <Badge className="border-slate-200 bg-white text-slate-700 dark:border-border dark:bg-secondary dark:text-gray-300">
            {Math.min(activity.length, 6)} recent
          </Badge>
        </div>
        {recentMovementExpanded && (
          <div className="grid gap-3">
            {activity.slice(0, 6).map((item, index) => (
              <RecentMovementItem key={`${item.type || 'activity'}-${item.time || index}-${item.user || 'unknown'}`} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
